import React, { useState, useEffect } from 'react';
import api from '../../../services/api';
import CepInput from '../../../components/shared/CepInput';
import ImageUploadField from '../../../components/shared/ImageUploadField';
import { maskPhone, maskCNPJ } from '../../../utils/masks';
import { validateCNPJ } from '../../../utils/validators';
import { Save, Building2, Plus, Trash2, CreditCard } from 'lucide-react';

export default function EmpresaForm() {
  const [form, setForm] = useState({ nome_fantasia: '', razao_social: '', cnpj: '', inscricao_estadual: '', email: '', telefone: '', whatsapp: '', cep: '', logradouro: '', numero: '', bairro: '', cidade: '', uf: '', endereco_entrega: '', observacoes: '', logo_url: '', dados_recebimento: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [cnpjError, setCnpjError] = useState('');
  const [newPix, setNewPix] = useState({ banco: '', conta_corrente: '', chave_pix: '' });

  useEffect(() => {
    api.get('/empresa').then(r => { if (r.data && Object.keys(r.data).length > 0) setForm(f => ({ ...f, ...r.data, dados_recebimento: r.data.dados_recebimento || [] })); }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const handleCep = (addr) => addr && setForm(f => ({ ...f, logradouro: addr.logradouro, bairro: addr.bairro, cidade: addr.cidade, uf: addr.uf }));

  const handleCnpjChange = (value) => {
    const masked = maskCNPJ(value);
    set('cnpj', masked);
    const digits = masked.replace(/\D/g, '');
    if (digits.length === 14) {
      setCnpjError(validateCNPJ(masked) ? '' : 'CNPJ inválido');
    } else {
      setCnpjError('');
    }
  };

  const addDadosRecebimento = () => {
    if (!newPix.chave_pix.trim()) return;
    setForm(f => ({ ...f, dados_recebimento: [...f.dados_recebimento, { ...newPix }] }));
    setNewPix({ banco: '', conta_corrente: '', chave_pix: '' });
  };

  const removeDadosRecebimento = (idx) => {
    setForm(f => ({ ...f, dados_recebimento: f.dados_recebimento.filter((_, i) => i !== idx) }));
  };

  const handleSave = async () => {
    if (form.cnpj && !validateCNPJ(form.cnpj)) {
      setCnpjError('CNPJ inválido — verifique o número antes de salvar');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await api.put('/empresa', form);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) { setError(err.userMessage || 'Erro ao salvar'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-6 h-6 border-4 border-brand-primary border-t-transparent rounded-full animate-spin" /></div>;
  const inputCls = "w-full rounded-lg border border-brand-border px-3 py-2 text-sm font-body focus:outline-none focus:ring-2 focus:ring-brand-accent-mid focus:border-brand-primary bg-white";

  return (
    <div data-testid="empresa-form">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-brand-accent-light rounded-xl flex items-center justify-center"><Building2 className="w-5 h-5 text-brand-primary" /></div>
        <div><h1 className="dashboard-heading text-2xl">Dados da Empresa</h1><p className="text-brand-text-muted text-sm font-body">Configurações gerais da empresa</p></div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 mb-4">{error}</div>}
      {success && <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-xl px-4 py-3 mb-4" data-testid="success-msg">Dados salvos com sucesso!</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="card-dashboard space-y-3">
          <h3 className="font-dashboard font-semibold text-brand-text">Identificação</h3>
          <div><label className="block text-xs text-brand-text-muted mb-1 font-body">Nome Fantasia *</label><input className={inputCls} value={form.nome_fantasia} onChange={e => set('nome_fantasia', e.target.value)} data-testid="field-nome-fantasia" /></div>
          <div><label className="block text-xs text-brand-text-muted mb-1 font-body">Razão Social</label><input className={inputCls} value={form.razao_social} onChange={e => set('razao_social', e.target.value)} /></div>
          <div>
            <label className="block text-xs text-brand-text-muted mb-1 font-body">CNPJ</label>
            <input className={`${inputCls} ${cnpjError ? 'border-red-400 focus:ring-red-300' : ''}`} value={form.cnpj} onChange={e => handleCnpjChange(e.target.value)} placeholder="00.000.000/0000-00" maxLength={18} data-testid="field-cnpj" />
            {cnpjError && <p className="text-xs text-red-600 mt-1 font-body" data-testid="cnpj-error">{cnpjError}</p>}
          </div>
          <div><label className="block text-xs text-brand-text-muted mb-1 font-body">Inscrição Estadual</label><input className={inputCls} value={form.inscricao_estadual} onChange={e => set('inscricao_estadual', e.target.value)} /></div>
          <div><label className="block text-xs text-brand-text-muted mb-1 font-body">E-mail</label><input type="email" className={inputCls} value={form.email} onChange={e => set('email', e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs text-brand-text-muted mb-1 font-body">Telefone</label><input className={inputCls} value={form.telefone} onChange={e => set('telefone', maskPhone(e.target.value))} /></div>
            <div><label className="block text-xs text-brand-text-muted mb-1 font-body">WhatsApp</label><input className={inputCls} value={form.whatsapp} onChange={e => set('whatsapp', maskPhone(e.target.value))} /></div>
          </div>
        </div>

        <div className="card-dashboard space-y-3">
          <h3 className="font-dashboard font-semibold text-brand-text">Endereço</h3>
          <div><label className="block text-xs text-brand-text-muted mb-1 font-body">CEP</label><CepInput value={form.cep} onChange={v => set('cep', v)} onAddressFound={handleCep} className={inputCls} /></div>
          <div><label className="block text-xs text-brand-text-muted mb-1 font-body">Logradouro</label><input className={inputCls} value={form.logradouro} onChange={e => set('logradouro', e.target.value)} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs text-brand-text-muted mb-1 font-body">Número</label><input className={inputCls} value={form.numero} onChange={e => set('numero', e.target.value)} /></div>
            <div><label className="block text-xs text-brand-text-muted mb-1 font-body">Bairro</label><input className={inputCls} value={form.bairro} onChange={e => set('bairro', e.target.value)} /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2"><label className="block text-xs text-brand-text-muted mb-1 font-body">Cidade</label><input className={inputCls} value={form.cidade} onChange={e => set('cidade', e.target.value)} /></div>
            <div><label className="block text-xs text-brand-text-muted mb-1 font-body">UF</label><input className={inputCls} value={form.uf} onChange={e => set('uf', e.target.value)} maxLength={2} /></div>
          </div>
          <div>
            <label className="block text-xs text-brand-text-muted mb-2 font-body">Logo da Empresa</label>
            <ImageUploadField value={form.logo_url} onChange={url => set('logo_url', url)} placeholder="Adicionar logotipo" />
          </div>
          <div><label className="block text-xs text-brand-text-muted mb-1 font-body">Observações</label><textarea className={`${inputCls} h-20 resize-none`} value={form.observacoes} onChange={e => set('observacoes', e.target.value)} /></div>
        </div>
      </div>

      {/* Dados de Recebimento */}
      <div className="card-dashboard mt-4" data-testid="dados-recebimento-card">
        <div className="flex items-center gap-2 mb-4">
          <CreditCard className="w-5 h-5 text-brand-primary" />
          <h3 className="font-dashboard font-semibold text-brand-text">Dados de Recebimento</h3>
        </div>

        {form.dados_recebimento.length > 0 && (
          <div className="space-y-2 mb-4">
            {form.dados_recebimento.map((d, i) => (
              <div key={i} className="flex items-center gap-3 bg-brand-bg rounded-lg px-3 py-2.5" data-testid="recebimento-item">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-body font-medium text-brand-text truncate">{d.chave_pix}</p>
                  <p className="text-xs text-brand-text-muted font-body">{[d.banco, d.conta_corrente].filter(Boolean).join(' · ')}</p>
                </div>
                <button onClick={() => removeDadosRecebimento(i)} className="p-1.5 rounded hover:bg-red-50 flex-shrink-0" data-testid="remove-recebimento-btn">
                  <Trash2 className="w-3.5 h-3.5 text-red-400" />
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="border border-dashed border-brand-border rounded-xl p-4">
          <p className="text-xs font-dashboard font-semibold text-brand-text-muted uppercase tracking-wide mb-3">Adicionar conta</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
            <div>
              <label className="block text-xs text-brand-text-muted mb-1 font-body">Banco</label>
              <input className={inputCls} placeholder="Ex: Nubank, Itaú..." value={newPix.banco} onChange={e => setNewPix(p => ({ ...p, banco: e.target.value }))} data-testid="new-banco-input" />
            </div>
            <div>
              <label className="block text-xs text-brand-text-muted mb-1 font-body">Conta Corrente</label>
              <input className={inputCls} placeholder="Ex: 12345-6" value={newPix.conta_corrente} onChange={e => setNewPix(p => ({ ...p, conta_corrente: e.target.value }))} data-testid="new-conta-input" />
            </div>
            <div>
              <label className="block text-xs text-brand-text-muted mb-1 font-body">Chave Pix *</label>
              <input className={inputCls} placeholder="CPF, e-mail, telefone ou aleatória" value={newPix.chave_pix} onChange={e => setNewPix(p => ({ ...p, chave_pix: e.target.value }))} data-testid="new-pix-input" />
            </div>
          </div>
          <button onClick={addDadosRecebimento} disabled={!newPix.chave_pix.trim()} className="flex items-center gap-2 px-4 py-2 rounded-lg btn-primary text-sm font-dashboard font-semibold disabled:opacity-50" data-testid="add-recebimento-btn">
            <Plus className="w-4 h-4" /> Adicionar
          </button>
        </div>
      </div>

      <button onClick={handleSave} disabled={saving} className="mt-6 px-6 py-2.5 rounded-lg btn-primary font-dashboard font-semibold text-sm flex items-center gap-2 disabled:opacity-60" data-testid="save-empresa-btn">
        <Save className="w-4 h-4" /> {saving ? 'Salvando...' : 'Salvar Dados'}
      </button>
    </div>
  );
}

